"use client";

import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import axios from "axios";
import { useBranches } from "@/hooks/api/useBranches";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { useProducts } from "@/hooks/api/useProducts";
import { usePurchaseOrderMutations } from "@/hooks/api/usePurchaseOrders";
import { useCreateSupplier, useSuppliers } from "@/hooks/api/useSuppliers";
import type { ProductRow } from "@/lib/types/product";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Line = { product: ProductRow; qty: number; rate: number };

export default function CreatePurchaseOrderPage() {
  const navigate = useNavigate();
  const { data: branches } = useBranches();
  const { branchesEnabled } = useBranchesEnabled();
  const { data: suppliers = [] } = useSuppliers();
  const createSupplier = useCreateSupplier();
  const [branchId, setBranchId] = useState<number | null>(null);
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const { create, send } = usePurchaseOrderMutations();
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);
  const [addSuppOpen, setAddSuppOpen] = useState(false);
  const [suppName, setSuppName] = useState("");
  const [suppPhone, setSuppPhone] = useState("");
  const [suppGstin, setSuppGstin] = useState("");
  const [suppEmail, setSuppEmail] = useState("");
  const [suppAddress, setSuppAddress] = useState("");
  const [suppErr, setSuppErr] = useState<string | null>(null);

  const bid = branchId ?? branches?.[0]?.id ?? null;
  const showBranchPicker = branchesEnabled && (branches?.length ?? 0) > 1;
  const { data: prodData } = useProducts({
    page: 1,
    per_page: 40,
    search: search || undefined,
    branch_id: branchesEnabled ? (bid ?? undefined) : undefined,
    stock_status: "all",
    enabled: branchesEnabled ? bid != null : true,
  });
  const products = useMemo(() => prodData?.data ?? [], [prodData?.data]);

  function addProduct(p: ProductRow) {
    setLines((prev) => {
      const i = prev.findIndex((x) => x.product.id === p.id);
      if (i >= 0) {
        const n = [...prev];
        n[i] = { ...n[i], qty: n[i].qty + 1 };

        return n;
      }

      return [...prev, { product: p, qty: 1, rate: p.selling_price || p.cost_price || 0 }];
    });
  }

  async function save(as: "draft" | "sent") {
    setSaveErr(null);
    setSaveOk(null);
    if (!bid || !supplierId || !lines.length) {
      setSaveErr("Branch, supplier, at least 1 line required.");
      return;
    }
    try {
      const po = await create.mutateAsync({
        supplier_id: supplierId,
        branch_id: bid,
        status: "draft",
        items: lines.map((l) => ({
          product_id: l.product.id,
          qty_ordered: l.qty,
          rate: l.rate,
          gst_rate: l.product.gst_rate,
        })),
      });
      if (as === "sent") {
        await send.mutateAsync(po.id);
        setSaveOk("Order placed (sent). Email sent to supplier.");
      } else {
        setSaveOk("Draft saved.");
      }
      navigate(`/purchase/orders/${po.id}`);
    } catch (e: unknown) {
      let msg = "Could not save purchase order.";
      if (axios.isAxiosError(e)) {
        const data = e.response?.data as { error?: string; message?: string } | undefined;
        msg = data?.error ?? data?.message ?? msg;
      }
      setSaveErr(msg);
    }
  }

  async function onAddSupplier() {
    setSuppErr(null);
    if (!suppName.trim()) {
      setSuppErr("Name required.");
      return;
    }
    try {
      const created = await createSupplier.mutateAsync({
        name: suppName,
        phone: suppPhone,
        gstin: suppGstin,
        email: suppEmail,
        address: suppAddress,
        is_active: 1,
      });
      setSupplierId(created.id);
      setAddSuppOpen(false);
      setSuppName("");
      setSuppPhone("");
      setSuppGstin("");
      setSuppEmail("");
      setSuppAddress("");
    } catch (e: unknown) {
      let msg = "Could not create supplier.";
      if (axios.isAxiosError(e)) {
        const data = e.response?.data as { error?: string; message?: string } | undefined;
        msg = data?.error ?? data?.message ?? msg;
      }
      setSuppErr(msg);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">New purchase order</h1>
        <Link
          to="/purchase/orders"
          className={cn(buttonVariants({ variant: "outline" }), "inline-flex h-9 items-center px-3")}
        >
          Back
        </Link>
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {showBranchPicker ? (
            <div>
              <Label>Branch</Label>
              <Select
                value={bid ? String(bid) : ""}
                onValueChange={(v) => setBranchId(Number(v))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Branch" />
                </SelectTrigger>
                <SelectContent>
                  {(branches ?? []).map((b) => (
                    <SelectItem key={b.id} value={String(b.id)}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className={showBranchPicker ? "" : "sm:col-span-2"}>
            <Label>Supplier</Label>
            <div className="mt-1 flex gap-2">
              <Select
                value={supplierId ? String(supplierId) : ""}
                onValueChange={(v) => setSupplierId(Number(v))}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={() => setAddSuppOpen(true)}>
                Add
              </Button>
            </div>
            {suppliers.length === 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                No suppliers yet. Add supplier to create PO.
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <Label>Search products</Label>
        <Input className="mt-1" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name / SKU…" />
        <div className="mt-2 max-h-48 space-y-1 overflow-auto rounded border p-2">
          {products.map((p) => (
            <button
              key={p.id}
              type="button"
              className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted"
              onClick={() => addProduct(p)}
            >
              {p.name} · ₹{p.selling_price}
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="mb-2 font-medium">Lines</h2>
        <div className="space-y-2">
          {lines.map((l) => (
            <div key={l.product.id} className="flex flex-wrap items-center gap-2 border-b py-2">
              <span className="min-w-0 flex-1 text-sm font-medium">{l.product.name}</span>
              <Input
                type="number"
                className="h-8 w-20"
                value={l.qty}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!Number.isFinite(v) || v <= 0) {
                    return;
                  }
                  setLines((prev) =>
                    prev.map((x) => (x.product.id === l.product.id ? { ...x, qty: v } : x)),
                  );
                }}
              />
              <Input
                type="number"
                className="h-8 w-24"
                value={l.rate}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!Number.isFinite(v)) {
                    return;
                  }
                  setLines((prev) =>
                    prev.map((x) => (x.product.id === l.product.id ? { ...x, rate: v } : x)),
                  );
                }}
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => setLines((p) => p.filter((x) => x.product.id !== l.product.id))}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={!bid || !supplierId || !lines.length || create.isPending}
          onClick={() => void save("draft")}
        >
          Save draft
        </Button>
        <Button
          variant="secondary"
          disabled={!bid || !supplierId || !lines.length || create.isPending || send.isPending}
          onClick={() => void save("sent")}
        >
          Save &amp; send email
        </Button>
      </div>

      {saveErr ? (
        <p className="text-sm text-destructive" role="alert">
          {saveErr}
        </p>
      ) : null}
      {saveOk ? (
        <p className="text-sm text-emerald-700" role="status">
          {saveOk}
        </p>
      ) : null}

      <Dialog open={addSuppOpen} onOpenChange={setAddSuppOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add supplier</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Name *</Label>
              <Input value={suppName} onChange={(e) => setSuppName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Phone</Label>
              <Input value={suppPhone} onChange={(e) => setSuppPhone(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>GSTIN</Label>
              <Input value={suppGstin} onChange={(e) => setSuppGstin(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Email</Label>
              <Input value={suppEmail} onChange={(e) => setSuppEmail(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Address</Label>
              <Input value={suppAddress} onChange={(e) => setSuppAddress(e.target.value)} />
            </div>

            {suppErr ? (
              <p className="text-sm text-destructive" role="alert">
                {suppErr}
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAddSuppOpen(false)}>
                Cancel
              </Button>
              <Button type="button" disabled={createSupplier.isPending} onClick={() => void onAddSupplier()}>
                {createSupplier.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
